import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  /** Array of image URLs. Nulls/undefineds are filtered out automatically. */
  images: (string | null | undefined)[];
  thumbnailSize?: number;
  alt?: string;
  /** Optional label shown at the top of the zoomed lightbox (e.g. "Batch BT-0110"). */
  caption?: string;
}

const LightboxGallery: React.FC<Props> = ({ images, thumbnailSize = 30, alt = 'Photo', caption }) => {
  const valid = images.filter((u): u is string => !!u);

  const [isOpen, setIsOpen]   = useState(false);
  const [idx, setIdx]         = useState(0);

  const prev  = useCallback(() => setIdx(i => (i - 1 + valid.length) % valid.length), [valid.length]);
  const next  = useCallback(() => setIdx(i => (i + 1) % valid.length),                [valid.length]);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      if (e.key === 'Escape')     close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, prev, next, close]);

  if (valid.length === 0) return <span style={{ color: '#cbd5e1' }}>—</span>;

  const hasMultiple = valid.length > 1;

  return (
    <>
      {/* ── Thumbnail ── */}
      <div
        onClick={() => { setIdx(0); setIsOpen(true); }}
        title={hasMultiple ? `${valid.length} photos — click to view` : 'Click to view'}
        style={{ position: 'relative', display: 'inline-block', cursor: 'zoom-in', lineHeight: 0 }}
      >
        <img
          src={valid[0]}
          alt={alt}
          style={{
            width:  thumbnailSize,
            height: thumbnailSize,
            objectFit: 'cover',
            borderRadius: '4px',
            border: hasMultiple ? '2px solid #3b82f6' : '1px solid #e2e8f0',
            display: 'block',
          }}
        />
        {hasMultiple && (
          <span style={{
            position: 'absolute', bottom: 2, right: 2,
            background: 'rgba(37,99,235,0.9)',
            color: 'white',
            fontSize: '0.48rem',
            fontWeight: 800,
            lineHeight: 1,
            padding: '1px 3px',
            borderRadius: '3px',
            pointerEvents: 'none',
          }}>
            +{valid.length - 1}
          </span>
        )}
      </div>

      {/* ── Lightbox (portal) ── */}
      {isOpen && ReactDOM.createPortal(
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          {/* Main image — stopPropagation so clicking it doesn't close */}
          <img
            src={valid[idx]}
            alt={`${alt} ${idx + 1}`}
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '85vh',
              objectFit: 'contain', borderRadius: '8px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',
            }}
          />

          {/* Caption + counter pill */}
          {(caption || hasMultiple) && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'fixed', top: '1.1rem', left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.55)', color: 'white',
                borderRadius: '999px', padding: '0.28rem 0.85rem',
                fontSize: '0.78rem', fontWeight: 700,
                pointerEvents: 'none', whiteSpace: 'nowrap',
              }}
            >
              {caption}{caption && hasMultiple ? '  ·  ' : ''}{hasMultiple ? `${idx + 1} / ${valid.length}` : ''}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={close}
            style={{
              position: 'fixed', top: '0.85rem', right: '0.85rem',
              background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
              color: 'white', width: 36, height: 36,
              cursor: 'pointer', fontSize: '1.2rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>

          {/* Left arrow */}
          {hasMultiple && (
            <button
              onClick={e => { e.stopPropagation(); prev(); }}
              style={{
                position: 'fixed', left: '1rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
                color: 'white', width: 44, height: 44,
                cursor: 'pointer', fontSize: '1.6rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ‹
            </button>
          )}

          {/* Right arrow */}
          {hasMultiple && (
            <button
              onClick={e => { e.stopPropagation(); next(); }}
              style={{
                position: 'fixed', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
                color: 'white', width: 44, height: 44,
                cursor: 'pointer', fontSize: '1.6rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ›
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default LightboxGallery;
